import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Calendar, Building2, ExternalLink, Megaphone, ChevronDown } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import ShareButtons from '@/components/ShareButtons';

const API = process.env.REACT_APP_BACKEND_URL;

const MediaPage = () => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetch(`${API}/api/partners/announcements`)
      .then(r => r.json())
      .then(data => setAnnouncements(data.announcements || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative pt-20 pb-12 lg:pt-28 lg:pb-16 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 text-violet-700 text-xs font-bold tracking-wide uppercase mb-6">
              <Megaphone className="w-3.5 h-3.5" /> Media
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-taxera-charcoal leading-tight mb-4" data-testid="media-title">
              News &amp; Announcements
            </h1>
            <p className="text-lg text-slate-500 max-w-2xl" data-testid="media-subtitle">
              The latest press releases, partner announcements, and company news from Taxera Technologies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Announcements */}
      <section className="py-16 lg:py-20" data-testid="media-announcements">
        <div className="max-w-5xl mx-auto px-6">
          {loading ? (
            <div className="space-y-8">
              {[1, 2].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-8 animate-pulse">
                  <div className="h-4 bg-slate-100 rounded w-1/3 mb-4" />
                  <div className="h-6 bg-slate-100 rounded w-2/3 mb-3" />
                  <div className="h-4 bg-slate-100 rounded w-full mb-2" />
                  <div className="h-4 bg-slate-100 rounded w-4/5" />
                </div>
              ))}
            </div>
          ) : announcements.length > 0 ? (
            <div className="space-y-8">
              {announcements.map((ann, i) => {
                const isExpanded = expanded[ann.id];

                return (
                  <motion.article
                    key={ann.id || i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="group bg-white rounded-2xl border border-slate-200 hover:border-taxera-blue/30 hover:shadow-xl transition-all overflow-hidden"
                    data-testid={`media-announcement-${ann.id || i}`}
                  >
                    {/* Header image */}
                    {ann.image_url && (
                      <div className="relative h-56 lg:h-72 overflow-hidden">
                        <img src={ann.image_url} alt={ann.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                        {ann.tier && (
                          <span className="absolute top-4 left-4 text-2xs font-bold uppercase tracking-wide text-white bg-taxera-blue/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                            {ann.tier}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="p-6 lg:p-8">
                      {/* Meta row */}
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex-1">
                          {!ann.image_url && ann.tier && (
                            <span className="inline-block text-2xs font-bold uppercase tracking-wide text-white bg-taxera-blue px-2.5 py-1 rounded-full mb-3">
                              {ann.tier}
                            </span>
                          )}
                          {(ann.published_at || ann.date) && (
                            <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-3">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(ann.published_at || ann.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          <h2 className="font-heading text-xl lg:text-2xl font-bold text-taxera-charcoal group-hover:text-taxera-blue transition-colors leading-snug mb-2">
                            <Link to={`/media/${ann.slug || ann.id}`} className="hover:underline" data-testid={`media-link-${ann.id}`}>
                              {ann.title}
                            </Link>
                          </h2>
                          {ann.partner_name && (
                            <div className="flex items-center gap-2 text-sm text-taxera-teal font-semibold">
                              <Building2 className="w-3.5 h-3.5" />
                              with {ann.partner_name}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0">
                          <ShareButtons
                            url={`${window.location.origin}/media/${ann.slug || ann.id}`}
                            title={ann.title}
                            summary={ann.summary}
                            compact
                          />
                        </div>
                      </div>

                      {/* Summary */}
                      <p className="text-base text-slate-600 leading-relaxed mb-4">
                        {ann.summary || ann.description}
                      </p>

                      {/* Expand/Collapse for full content */}
                      {ann.content && (
                        <>
                          <button
                            onClick={() => toggleExpand(ann.id)}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-taxera-blue hover:underline mb-4"
                            data-testid={`media-expand-${ann.id || i}`}
                          >
                            {isExpanded ? 'Show less' : 'Read full announcement'}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                              >
                                <div className="text-sm text-slate-500 leading-relaxed space-y-4 mb-5 border-t border-slate-100 pt-5">
                                  {ann.content.split('\n\n').map((para, j) => {
                                    if (para.startsWith('\u201C') || para.startsWith('"')) {
                                      const dashIdx = para.lastIndexOf('\u2014');
                                      const quoteText = dashIdx > 0 ? para.substring(0, dashIdx).trim() : para;
                                      const attribution = dashIdx > 0 ? para.substring(dashIdx) : '';
                                      return (
                                        <blockquote key={`q-${para.slice(0, 30)}`} className="border-l-3 border-taxera-teal pl-5 py-3 bg-taxera-teal/[0.03] rounded-r-xl">
                                          <p className="text-slate-600 italic leading-relaxed">{quoteText}</p>
                                          {attribution && (
                                            <p className="text-xs font-semibold text-taxera-charcoal mt-2 not-italic">{attribution}</p>
                                          )}
                                        </blockquote>
                                      );
                                    }
                                    return <p key={`p-${para.slice(0, 30)}`}>{para}</p>;
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        {ann.source_url ? (
                          <a
                            href={ann.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-taxera-blue hover:underline"
                            data-testid={`media-partner-url-${ann.id || i}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Partner press release
                          </a>
                        ) : <div />}
                        {ann.announced_by && (
                          <p className="text-xs text-slate-400">
                            <span className="text-slate-500 font-medium">{ann.announced_by}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No announcements yet. Check back soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-4">
            Interested in partnering with Taxera?
          </h2>
          <p className="text-slate-500 mb-6">
            We work with advisory firms, system integrators, and technology partners across the globe.
          </p>
          <a
            href="/partners"
            className="inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full font-semibold text-sm hover:shadow-lg transition-all"
            data-testid="media-partner-cta"
          >
            Explore the Partner Program <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </PageLayout>
  );
};

export default MediaPage;
